// @ts-nocheck
import { useState } from "react";
import { MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { FeedbackForm } from "./FeedbackForm";
// html2canvas is lazy-loaded on demand (48 kB gzip — only needed for screenshot capture)

interface FeedbackDialogProps {
  variant?: "ghost" | "default" | "outline";
  size?: "default" | "sm" | "icon";
  showLabel?: boolean;
}

export const FeedbackDialog = ({
  variant = "ghost",
  size = "icon",
  showLabel = false,
}: FeedbackDialogProps) => {
  const [open, setOpen] = useState(false);
  const [capturedScreenshot, setCapturedScreenshot] = useState<any | null>(null);

  const captureScreenshot = async () => {
    try {
      const { default: html2canvas } = await import("html2canvas");
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        allowTaint: true,
        logging: false,
      });

      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
        }, "image/png");
      });

      const file = new File([blob], `screenshot-${Date.now()}.png`, {
        type: "image/png",
      });

      setCapturedScreenshot(file);
    } catch (error) {
      console.error("Failed to capture screenshot:", error);
      setCapturedScreenshot(null);
    }
  };

  const handleTriggerClick = async () => {
    await captureScreenshot();
    setOpen(true);
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      setCapturedScreenshot(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant={variant}
          size={size}
          title="Send Feedback"
          aria-label="Send feedback"
          onClick={handleTriggerClick}
        >
          <MessageSquare className={showLabel ? "mr-2 h-4 w-4" : "h-4 w-4"} />
          {showLabel && <span>Feedback</span>}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Send Feedback</DialogTitle>
          <DialogDescription>
            Report bugs, request features, or share your thoughts with us
          </DialogDescription>
        </DialogHeader>
        <FeedbackForm 
          onSuccess={() => setOpen(false)} 
          initialScreenshot={capturedScreenshot} 
        />
      </DialogContent>
    </Dialog>
  );
};
